import {
  Backdrop,
  Button,
  Box,
  Grid,
  styled,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAddKontakReferensi } from "../../../../api/effects/mutations/KontakReferensi/addKontakReferensi";
import { useEditKontakReferensi } from "../../../../api/effects/mutations/KontakReferensi/editKontakReferensi";
import { useFetchKontakReferensi } from "../../../../api/effects/mutations/KontakReferensi/useFetchKontakReferensi";
import { InputTextFieldForm } from "../../../../components/InputTextFieldForm";

const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const ScrollGrid = styled(Grid)(({ theme }) => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

type FormValues = {
  nama: string;
  hubungan: string;
  telepon: string;
  pekerjaan: string;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  ID?: number;
};

export default function KontakReferensiModal({
  ID = -1,
  open,
  handleClose,
}: Props) {
  // Zod validation schema
  const schema = z.object({
    nama: z.string({ required_error: "Nama field is required" }).min(1, { message: "Nama is required" }),
    hubungan: z.string({ required_error: "Hubungan field is required" }).min(1, { message: "Hubungan is required" }),
    telepon: z.string({ required_error: "Telepon field is required" })
      .min(1, { message: "Telepon field  is required" })
      .regex(/^[0-9]+$/, { message: "Nomer telepon hanya boleh berisi angka." })
      .startsWith("08", { message: 'Nomer telepon harus dimulai dengan angka "08".' })
      .min(10, { message: "Nomer telepon terlalu pendek. Panjang minimal 10 digit." })
      .max(15, { message: "Nomer telepon terlalu panjang. Panjang maksimal 15 digit." }),
    pekerjaan: z.string({ required_error: "Pekerjaan field is required" }).min(1, { message: "Pekerjaan is required" }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      nama: '',
      hubungan: '',
      telepon: '',
      pekerjaan: ''
    }
  });

  const { mutateAsync: GetKontakReferensiData } = useFetchKontakReferensi({
    onSuccess(data) {
      setValue('nama', data.NAMA);
      setValue('telepon', data.TELEPON);
      setValue('hubungan', data.HUBUNGAN);
      setValue('pekerjaan', data.PEKERJAAN);
    },
    onError() {
      console.log("Error");
    },
  });

  // Reset form when modal opens or ID changes
  useEffect(() => {
    if (open) {
      // If opening a new modal, reset to default values
      if (ID === -1) {
        reset();
      }
      // If editing an existing contact, fetch and populate data
      else if (ID > 0) {
        GetKontakReferensiData(ID);
      }
    }
  }, [ID, open, reset]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const { mutateAsync: AddKontakReferensi, isLoading: isAdding } = useAddKontakReferensi({
    onSuccess() {
      handleModalClose();
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  const { mutateAsync: EditKontakReferensi, isLoading: isEditing } = useEditKontakReferensi({
    onSuccess() {
      handleModalClose();
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    const payload = {
      NAMA: data.nama,
      HUBUNGAN: data.hubungan,
      PEKERJAAN: data.pekerjaan,
      TELEPON: data.telepon,
    };

    if (ID > 0) {
      await EditKontakReferensi({
        ID: ID,
        ...payload
      });
    } else {
      await AddKontakReferensi(payload);
    }
  };

  const handleModalClose = () => {
    handleClose();
    reset(); // Reset form when closing the modal
  };

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>
        <SquareGrid container>
          <ScrollGrid container>
            <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
              paddingBottom={
                {
                  xs: 5,
                  sm: 5,
                  md: 5,
                  lg: 5,
                  xl: 5
                }}>
              <Typography variant="subtitle1" color="black">
                {ID === -1 ? "Tambah Kontak Referensi" : "Ubah Kontak Referensi"}
              </Typography>
            </Grid>
            <Grid container justifyContent="flex-end" item xs={6}>
              <Button onClick={handleModalClose}>Close</Button>
            </Grid>
            <InputTextFieldForm
              name="nama"
              control={control}
              label="Nama"
              error={errors.nama}
            />
            <InputTextFieldForm
              name="hubungan"
              control={control}
              label="Hubungan"
              error={errors.hubungan}
            />
            <InputTextFieldForm
              name="telepon"
              control={control}
              label="Nomor Telepon"
              error={errors.telepon}
            />
            <InputTextFieldForm
              name="pekerjaan"
              control={control}
              label="Pekerjaan"
              error={errors.pekerjaan}
            />
            <Grid container justifyContent="center" item lg={12}
              paddingTop={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
              paddingBottom={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}>
              <FullButton variant="contained" type="submit" disabled={isAdding || isEditing}>
                {(isAdding || isEditing) ? "Saving..." : "Save"}
              </FullButton>
            </Grid>
          </ScrollGrid>
        </SquareGrid>
      </Box>
    </Backdrop>
  );
}